import { CreditsPlusIcon } from '@components/Icons/CreditsPlusIcon';
import LogOutIcon from '@components/Icons/LogOutIcon';
import { ProfileAvatar } from '@components/UserMenu/UserMenu';
import { getAvatarInitial } from '@lib/utils/colorUtils';
import { SECOND } from '@lib/utils/time';
import { formatCreditsWithUnit } from '@lib/utils/units';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import { ButtonBase, Popover } from '@mui/material';
import { billingStore } from '@stores/Billing';
import { trackingStore } from '@stores/Tracking';
import { userStore } from '@stores/User';
import classNames from 'classnames';
import { observer } from 'mobx-react';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CreditsBreakdown } from '../CreditsBreakdown/CreditsBreakdown';
import css from './AccountFooter.module.scss';

/**
 * Bottom-of-sidebar account footer: avatar + name, with the plan and remaining
 * credits on a secondary line (Claude-style). Clicking opens an upward popover
 * with the full credits breakdown and account actions — folding what used to be
 * the standalone CreditsBox hover card and the page-header UserMenu into one
 * place. Collapsed (icon-rail) shows the avatar with a small credit pill beneath.
 */
export const AccountFooter = observer(function AccountFooter({
  opened,
  onNavigate,
}: {
  opened: boolean;
  /** Called after any action that leaves the menu (settings, breakdown links) —
   *  the mobile overlay uses it to close the drawer behind the navigation. */
  onNavigate?: () => void;
}) {
  const user = userStore.data;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const nav = useNavigate();
  const location = useLocation();

  // Keep credits fresh on refocus, same cheap localStorage-then-maybe-fetch the
  // old CreditsBox did (it owned this before the footer replaced it).
  useEffect(() => {
    const onFocus = () => {
      billingStore.loadBillingFromLocalStorage();
      if (billingStore.isBillingDataStale(60 * SECOND)) billingStore.fetch();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, []);

  if (!user) return null;

  const credits = billingStore.creditsLeft;
  const creditsLabel = credits !== null ? formatCreditsWithUnit(credits, 'down') : null;
  const planName = billingStore.data?.subscription?.product?.name || 'No plan';
  const displayName =
    user.name?.nickname ||
    [user.name?.first, user.name?.last].filter(Boolean).join(' ') ||
    user.email ||
    'Account';

  const openMenu = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
    trackingStore.userMenuOpened();
  };
  const closeMenu = () => {
    setAnchorEl(null);
    trackingStore.userMenuClosed();
  };

  const goToSettings = () => {
    closeMenu();
    trackingStore.userMenuItemClicked({ item: 'settings', destination: '/settings' });
    if (location.pathname !== '/settings') nav('/settings');
    onNavigate?.();
  };
  const logout = () => {
    trackingStore.userMenuItemClicked({ item: 'logout' });
    userStore.logout();
  };
  const openExternal = (item: string, url: string) => {
    trackingStore.userMenuItemClicked({ item, destination: url });
    window.open(url, '_blank');
  };

  return (
    <>
      <ButtonBase
        type="button"
        className={classNames(css.footer, !opened && css.collapsed, open && css.active)}
        onClick={openMenu}
        aria-label="Account menu"
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <ProfileAvatar name={user.email} initial={getAvatarInitial(user)} />
        {/* Collapsed rail shows the avatar only — credits live in the popover. */}
        {opened && (
          <span className={css.text}>
            <span className={css.name}>{displayName}</span>
            <span className={css.secondary}>
              {planName}
              {creditsLabel && (
                <>
                  <span className={css.dot}>·</span>
                  <CreditsPlusIcon />
                  <span className={css.creditsValue}>{creditsLabel}</span>
                </>
              )}
            </span>
          </span>
        )}
      </ButtonBase>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        classes={{ paper: css.popoverPaper }}
      >
        <div className={css.menu}>
          <CreditsBreakdown
            source="account-footer"
            embedded
            onNavigate={() => {
              closeMenu();
              onNavigate?.();
            }}
          />
          <div className={css.actions}>
            <button type="button" className={css.action} onClick={goToSettings}>
              <SettingsRoundedIcon sx={{ fontSize: 20 }} />
              Settings
            </button>
            <button type="button" className={css.action} onClick={logout}>
              <LogOutIcon />
              Log out
            </button>
          </div>
          <div className={css.legal}>
            <button
              type="button"
              className={css.legalItem}
              onClick={() => openExternal('privacy', 'https://starzero.ai/privacy/')}
            >
              Privacy
            </button>
            <button
              type="button"
              className={css.legalItem}
              onClick={() => openExternal('terms', 'https://starzero.ai/terms/')}
            >
              Terms
            </button>
            <button
              type="button"
              className={classNames(css.legalItem, css.legalIcon)}
              aria-label="LinkedIn"
              onClick={() =>
                openExternal('linkedin', 'https://www.linkedin.com/company/it-just-works-inc')
              }
            >
              <LinkedInIcon sx={{ fontSize: 18 }} />
            </button>
          </div>
        </div>
      </Popover>
    </>
  );
});
